/**
 * Sales Performance stage 5: reconciliation. Newgenus's sales lines (IQ's STOCTRAN) are compared, day by day, with
 * what Statistics holds for the same day. Pure and side-effect free: the nightly job, the dev comparison script and
 * the page's warning share it (the backend keeps a drift-guarded mirror).
 *
 * What is compared is NETT INVOICED SALES excluding VAT: invoices (IN) less credit notes (CN). Open sales orders are
 * left out of Statistics' side because STOCTRAN holds only invoiced lines. The two sources can still legitimately
 * differ (a day's boundary, how a credit is dated, debtor codes other than IN and CN), which is exactly what the
 * percentage is there to show; nothing here is tuned to make them agree.
 */
import { netLine, SalesLine } from './sales-performance';

/** More than this share of Statistics' figure is a warning (0.5%). */
export const RECONCILE_THRESHOLD = 0.005;

const cents = (n: number) => Math.round(n * 100) / 100;

/** Our side: nett invoiced sales excluding VAT per date, credit notes netted, stock moves left out. */
export function stoctranDayNet(lines: SalesLine[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const raw of lines) {
        const l = netLine(raw);
        if (l) out[l.date] = (out[l.date] || 0) + l.amount;
    }
    for (const d of Object.keys(out)) out[d] = cents(out[d]);
    return out;
}

/** The fields of a Statistics day summary transaction (`fullSalesToday`) this reads. */
export interface SummaryTxLike {
    type?: string;
    transactionType?: string;
    userKey?: number | string;
    date?: unknown;
    invoicedGross?: number;
    invoicedTax?: number;
    creditNoteGross?: number;
    creditNoteTax?: number;
    salesOrderNett?: number;
}

const num = (n: unknown) => (typeof n === 'number' && isFinite(n) ? n : 0);

function millis(value: unknown): number | null {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value.getTime();
    if (value && typeof value === 'object') {
        const v = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
        if (typeof v.toDate === 'function') return v.toDate().getTime();
        if (typeof v.seconds === 'number') return v.seconds * 1000;
        if (typeof v._seconds === 'number') return v._seconds * 1000;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const t = new Date(value).getTime();
        return isNaN(t) ? null : t;
    }
    return null;
}

/**
 * Statistics' side: (invoiced - VAT) - (credited - VAT) for invoice and debtor-ledger transactions coded IN or CN,
 * per company day (`dayOf` turns an instant into the company's YYYY-MM-DD). Rounded per salesperson, as the page
 * does, so a day agrees with the Nett sales it shows. Sales orders are not invoices and are left out.
 */
export function statisticsInvoicedNet(txs: SummaryTxLike[], dayOf: (ms: number) => string): Record<string, number> {
    const per = new Map<string, { debit: number; debitTax: number; credit: number; creditTax: number }>();
    for (const tx of txs) {
        if (tx.type !== 'invoice' && tx.type !== 'debtTran') continue;
        if (tx.transactionType !== 'IN' && tx.transactionType !== 'CN') continue;
        const at = millis(tx.date);
        if (at === null) continue;
        const key = `${dayOf(at)}|${tx.userKey ?? ''}`;
        const r = per.get(key) || { debit: 0, debitTax: 0, credit: 0, creditTax: 0 };
        r.debit += num(tx.invoicedGross);
        r.debitTax += num(tx.invoicedTax);
        r.credit += num(tx.creditNoteGross);
        r.creditTax += num(tx.creditNoteTax);
        per.set(key, r);
    }
    const out: Record<string, number> = {};
    for (const [key, r] of per) {
        const day = key.slice(0, key.indexOf('|'));
        out[day] = (out[day] || 0) + cents(r.debit) - cents(r.debitTax) - (cents(r.credit) - cents(r.creditTax));
    }
    for (const d of Object.keys(out)) out[d] = cents(out[d]);
    return out;
}

export interface DayRecon {
    date: string;
    /** nett invoiced sales from the sales lines */
    ours: number;
    /** the same from Statistics; null when Statistics holds nothing for the day */
    statistics: number | null;
    diff: number | null;
    /** |diff| as a share of Statistics' figure; null when it cannot be worked out */
    pct: number | null;
    over: boolean;
    comparable: boolean;
}

export function reconcileDay(date: string, ours: number, statistics: number | null): DayRecon {
    if (statistics === null)
        return { date, ours, statistics: null, diff: null, pct: null, over: false, comparable: false };
    const diff = cents(ours - statistics);
    if (statistics === 0)
        return { date, ours, statistics, diff, pct: diff === 0 ? 0 : null, over: diff !== 0, comparable: true };
    const pct = Math.abs(diff) / Math.abs(statistics);
    // a hair of tolerance so exactly 0.5% (1005 against 1000) is not flagged by floating-point noise
    return { date, ours, statistics, diff, pct, over: pct > RECONCILE_THRESHOLD + 1e-12, comparable: true };
}

export interface MonthRecon {
    days: number;
    comparable: number;
    ours: number;
    statistics: number;
    diff: number;
    pct: number | null;
    daysOver: number;
}

/** A period's days summed: only days Statistics has a figure for count, on both sides. */
export function reconcileMonth(days: DayRecon[]): MonthRecon {
    const used = days.filter((d) => d.comparable);
    const ours = cents(used.reduce((n, d) => n + d.ours, 0));
    const statistics = cents(used.reduce((n, d) => n + (d.statistics as number), 0));
    const diff = cents(ours - statistics);
    return {
        days: days.length,
        comparable: used.length,
        ours,
        statistics,
        diff,
        pct: statistics !== 0 ? Math.abs(diff) / Math.abs(statistics) : diff === 0 ? 0 : null,
        daysOver: used.filter((d) => d.over).length,
    };
}

const MONTH_NAMES = 'January February March April May June July August September October November December'.split(' ');

/**
 * Where Statistics keeps a day: the month document `{year}-{MonthName}` and, in its `summary-by-day` collection,
 * one document per salesperson whose id starts with `{year}-{MonthName}-{DD}-`.
 */
export function statisticsDayPath(day: string): { month: string; prefix: string } {
    const [y, m, d] = day.split('-').map(Number);
    const month = `${y}-${MONTH_NAMES[m - 1]}`;
    return { month, prefix: `${month}-${String(d).padStart(2, '0')}-` };
}
